import { CloudDownloadOutlined, CloudUploadOutlined } from '@mui/icons-material'
import { Select, Typography } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { Button, Table } from 'antd';
import { useEffect, useState, useMemo } from 'react';
import { MaterialsModel } from '../../models/MaterialModel';
import { useNavigate } from "react-router-dom";
import useLoading from "../../hooks/useLoading";
import { useCompany } from '../../hooks/useCompany';
import { api } from '../../services/axios';
import { toast } from 'react-toastify';
import { IdentityEnum } from '../../Enums/ExpenditureEnum';
import ModalDelete from '../../components/Modal/ModalDelete/index.tsx'
import { ModalDeleteMassiveOtherExpenditure } from "../../components/OtherExpenditure/ModalDeleteMassiveOtherExpenditure";
import { SetFileName } from "../../utils/SetFileName";
import useModal from "../../hooks/useModal";
import { UploadMaterialAPI } from '../../API/MaterialAPI';
import { IResponseProps } from '../../interfaces/IResponseProps';

import { UploadModal } from '../../components/Shared/UploadModal';


export default function Materials() {
    const { toggleLoading, loading } = useLoading();
    const [total, setTotal] = useState<number>(0);
    const [material, setMaterials] = useState<MaterialsModel[]>([]);
    const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string>("");
    const { isShowing: isShowingExcelModal, toggle: toggleExcelModal } = useModal();
    const [fileMaterial, setFileMaterial] = useState<Blob>();
    const { company } = useCompany();
    const [take, setTake] = useState(7);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [isMassiveDeleteModalOpen, setIsMassiveDeleteModalOpen] = useState(false);
    const [filterCnpj, setFilterCnpj] = useState<string>("");
    const navigate = useNavigate();
    const { Text } = Typography;

    function formatCurrency(value: number | string) {
        const num = Number(value ?? 0);
        return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    function goToEditPage(id: string) {
        navigate(`/${company.serviceId}/createdExpenditure/materials/${id}`);
    }

    const formatDate = (dateString: Date) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    };

    const filters = useMemo(() => {
        const filtersIdentity: { text: string; value: number }[] = [];
        const filtersProjectName: { text: string; value: string }[] = [];
        const filtersSupplier: { text: string; value: string }[] = [];
        const filtersNDoc: { text: string; value: string }[] = [];
        const filtersAccountingDate: { text: string; value: string }[] = [];
        const filtersAccountedValue: { text: string; value: string }[] = [];

        if (!material || material.length === 0) {
            return { filtersIdentity, filtersProjectName, filtersSupplier, filtersNDoc, filtersAccountingDate, filtersAccountedValue };
        }

        material.forEach(item => {
            if (!filtersIdentity.some(filter => filter.value === item.identity)) {
                filtersIdentity.push({
                    text: IdentityEnum[item.identity] || 'Desconhecido',
                    value: item.identity,
                });
            }
            if (!filtersProjectName.some(filter => filter.value === item.projectName)) {
                filtersProjectName.push({
                    text: item.projectName,
                    value: item.projectName,
                });
            }
            if (!filtersSupplier.some(filter => filter.value === item.supplier)) {
                filtersSupplier.push({
                    text: item.supplier,
                    value: item.supplier,
                });
            }
            if (!filtersNDoc.some(filter => filter.value === item.nDoc)) {
                filtersNDoc.push({
                    text: item.nDoc,
                    value: item.nDoc,
                });
            }
            const formattedDate = formatDate(item.accountingDate);
            if (!filtersAccountingDate.some(filter => filter.value === formattedDate)) {
                filtersAccountingDate.push({
                    text: formattedDate,
                    value: formattedDate,
                });
            }
            if (!filtersAccountedValue.some(filter => filter.value === String(item.accountedValue))) {
                filtersAccountedValue.push({
                    text: formatCurrency(item.accountedValue),
                    value: String(item.accountedValue),
                });
            }
        });

        return { filtersIdentity, filtersProjectName, filtersSupplier, filtersNDoc, filtersAccountingDate, filtersAccountedValue };
    }, [material]);

    const handleDownloadFile = (url: string, filename: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleApiError = (error: any) => {
        const message = error.response?.data?.message || error.message || 'Erro desconhecido';
        toast.error(message);
    };

    async function getAllMaterials(page?: number, take?: number) {
        try {
            toggleLoading(true);
            const text = "\"\"";
            const response = await api.get(`Material/GetAllPaginatedByService/${company.serviceId}/${page ?? 1}/${take ?? 7}/${text}`);
            if (response.data.statusCode === 200) {
                setMaterials(response.data.data.items);
                setTotal(response.data.data.total);
            }
        } catch (error: any) {
            handleApiError(error);
        } finally {
            toggleLoading(false);
        }
    }

    async function downloadMaterial() {
        try {
            toggleLoading(true);
            const response = await api.get(`Material/DownloadMaterials/${company.serviceId}/${company.companyId}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const filename = SetFileName(company.title, company.year.toString(), "MateriaisDeConsumo", "xlsx");
            handleDownloadFile(url, filename);
            toast.success("Download realizado com sucesso!");
        } catch (error: any) {
            handleApiError(error);
        } finally {
            toggleLoading(false);
        }
    }

    async function handleUploadMaterial() {
        try {
            toggleLoading(true);
            toggleExcelModal();

            const formData = new FormData();
            if (fileMaterial) {
                formData.append('file', fileMaterial);
            }
            formData.append('taxNumber', company.taxNumber);
            formData.append('serviceId', company.serviceId);
            formData.append('year', company.year.toString());

            const resp = await UploadMaterialAPI(formData);
            if (resp.statusCode === 200) {
                toast.success("Upload realizado com sucesso!");
                getAllMaterials(1);
            }
        } catch (err: any) {
            handleApiError(err);
        } finally {
            toggleLoading(false);
        }
    }


    async function DeleteItem(id: string) {
        try {
            toggleLoading(true);
            const material = { MaterialId: id };
            const response = await api.delete("Material/DeleteItem", { data: material });
            if (response.data.statusCode === 200) {
                toast.success("Material deletado com sucesso!");
                setIsModalDeleteOpen(false);
                getAllMaterials(currentPage, take);
            }
        } catch (error: any) {
            handleApiError(error);
        } finally {
            toggleLoading(false);
        }
    }

    async function deleteMassiveMaterials() {
        toggleLoading(true);
        try {
            await Promise.all(
                selectedRowKeys.map(id =>
                    api.delete("Material/DeleteItem", { data: { MaterialId: id.toString() } })
                )
            );
            toast.success("Materiais deletados com sucesso!");
            setSelectedRowKeys([]);
            getAllMaterials(currentPage, take);
        } catch (error) {
            toast.error("Ocorreu um erro ao tentar deletar os materiais selecionados.");
        } finally {
            toggleLoading(false);
            setIsMassiveDeleteModalOpen(false);
        }
    }

    const handleShowSizeChange = (current: any, size: any) => {
        setTake(size);
        setCurrentPage(current);
        getAllMaterials(current, size);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => setSelectedRowKeys(newSelectedRowKeys),
    };

    const materialsColumns: TableProps<MaterialsModel>['columns'] = [
        {
            title: "Identidade",
            key: 'identity',
            dataIndex: 'identity',
            filters: filters.filtersIdentity,
            render: (value: number) => (
                <div>
                    <p>
                        {IdentityEnum[value] || 'Desconhecido'}
                    </p>
                </div>
            ),
            onFilter: (value, record) => record.identity === Number(value),
            sorter: (a, b) => a.identity - b.identity,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Nome do projeto',
            dataIndex: 'projectName',
            key: 'projectName',
            filters: filters.filtersProjectName,
            onFilter: (value, record) => record.projectName.includes(value as string),
            sorter: (a, b) => a.projectName.localeCompare(b.projectName),
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Fornecedor',
            dataIndex: 'supplier',
            key: 'supplier',
            filters: filters.filtersSupplier,
            onFilter: (value, record) => record.supplier.includes(value as string),
            sorter: (a, b) => a.supplier.localeCompare(b.supplier),
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Nº Documento',
            dataIndex: 'nDoc',
            key: 'nDoc',
            filters: filters.filtersNDoc,
            onFilter: (value, record) => record.nDoc.includes(value as string),
            sorter: (a, b) => a.nDoc.localeCompare(b.nDoc),
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Data da Contabilização',
            dataIndex: 'accountingDate',
            key: 'accountingDate',
            render: (value) => (
                <div>
                    <p>
                        {formatDate(value)}
                    </p>
                </div>
            ),
            filters: filters.filtersAccountingDate,
            onFilter: (value, record) => {
                const recordDate = new Date(record.accountingDate);
                const filterDate = new Date(value as string);
                return recordDate.toDateString() === filterDate.toDateString();
            },
            sorter: (a, b) => new Date(a.accountingDate).getTime() - new Date(b.accountingDate).getTime(),
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Valor Contabilizado',
            dataIndex: 'accountedValue',
            key: 'accountedValue',
            render: (value) => (
                <div>
                    <p>{formatCurrency(value)}</p>
                </div>
            ),
            sorter: (a, b) => Number(a.accountedValue) - Number(b.accountedValue),
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Ações',
            dataIndex: 'id',
            key: 'id',
            render: (value) => (
                <div>
                    <Button type="text" size="small" style={{ height: "24px", width: "24px" }} onClick={() => goToEditPage(value)}><EditOutlined className="blue" /></Button>
                    <Button type="text" size="small" style={{ height: "24px", width: "24px" }} onClick={() => { setItemToDelete(value); setIsModalDeleteOpen(true); }}><DeleteOutlined className="red-delete" /></Button>
                </div>
            )
        }
    ];
    useEffect(() => {
        getAllMaterials()
    }, [])
    return (
        <>
            <div className='d-flex justify-content-between align-items-center mt-4 mb-4'>
                <div className='flex justify-content-start'>
                    <Select
                        className='me-2 material-buttons'
                        placeholder='Selecione um CNPJ cadastrado...'
                        onChange={(value) => setFilterCnpj(value || "")}
                        dropdownStyle={{ maxHeight: 300, overflowY: 'auto' }}
                        style={{ width: "274px", height: "40px" }}
                        allowClear
                    />
                    <Button className='material-button-blue' type='primary' style={{ height: "40px" }} onClick={() => getAllMaterials(1, take)}>
                        <div className='text-white'>
                            Filtrar
                        </div>
                    </Button>
                </div>

                <div className='d-flex justify-content-end align-items-center'>
                    <Button icon={<CloudDownloadOutlined />} size="large" className='me-2 material-buttons' onClick={downloadMaterial}>Download</Button>
                    <Button icon={<CloudUploadOutlined />} size="large" className='me-2 material-buttons' onClick={toggleExcelModal}>Upload</Button>
                    <Button
                        type='primary'
                        size="large"
                        className='me-2 material-buttons material-button-blue'
                        onClick={() => navigate(`/${company.serviceId}/createdExpenditure/materials`)}
                    >
                        <div className='text-white'>
                            <PlusOutlined className='me-2' />
                            Inserir Dispêndio
                        </div>
                    </Button>
                    <Button
                        size="large"
                        danger
                        icon={<DeleteOutlined className='text-red' />}
                        className='me-2 material-buttons'
                        disabled={selectedRowKeys.length === 0}
                        onClick={() => setIsMassiveDeleteModalOpen(true)}
                    >
                        <span className='text-red'>Delete Massivo</span>
                    </Button>
                </div>
            </div>
            <Table
                loading={loading}
                rowKey="id"
                rowSelection={rowSelection}
                pagination={{
                    total: total,
                    showSizeChanger: true,
                    showTotal: (total) => <Text>Total {total} items</Text>,
                    pageSize: take,
                    current: currentPage,
                    onShowSizeChange: (page, size) => handleShowSizeChange(page, size),
                    onChange: (page, size) => {
                        handleShowSizeChange(page, size);
                    },

                }}
                columns={materialsColumns}
                dataSource={material}
            />
            {isShowingExcelModal &&
                <UploadModal
                    action={handleUploadMaterial}     
                    isShowing={isShowingExcelModal}
                    toggle={toggleExcelModal}
                    setFile={setFileMaterial}
                />
            }
            <ModalDelete setOpen={setIsModalDeleteOpen} id={itemToDelete} operation={DeleteItem} itemName='Material' open={isModalDeleteOpen} />
            <ModalDeleteMassiveOtherExpenditure
                isOpen={isMassiveDeleteModalOpen}
                setOpen={setIsMassiveDeleteModalOpen}
                operation={deleteMassiveMaterials}
                itemsCount={selectedRowKeys.length}
            />
        </>
    )
}
